/**
 * useLocalModelInstalls Hook
 *
 * Real installation state for a set of locally-installable AI models, backed by
 * the component API (`/api/v1/components`, api_server/local_routes/components/)
 * — the same endpoints the Components/Setup screens use.
 *
 * This is the single implementation behind every "this model isn't on disk yet"
 * gate in the app (sound effects via SFX_COMPONENT_IDS, voice cloning via
 * useClonedVoicesForTTS). Each feature binds it to its own component ids; none
 * of them re-implement the fetch/compat/install/SSE logic (RULE #-2).
 *
 * Two properties of this hook are load-bearing for the gates built on it:
 *
 *  1. It reports the component API's LIVE state, never the text of a backend
 *     error. A gate therefore behaves identically before the user's first
 *     attempt (proactive) and after a stale failure for a model that is still
 *     not installed (reactive).
 *  2. `stateKnown` tells the caller when that state is actually KNOWN.
 *     `installedModels` is empty before the request resolves and stays empty if
 *     it fails, so treating "not in the set" as "not installed" without this
 *     flag would offer a fresh multi-GB download for a model already on disk.
 *     While the state is unknown, callers must let the normal flow run and let
 *     the backend decide — a far cheaper mistake.
 *
 * Only active in desktop mode (Electron) — `/api/v1/components` is local-only
 * (RULE #19), so in serverless browser mode this stays empty, silent, and
 * `stateKnown` never becomes true (the cloud runs these models remotely, so
 * there is nothing to install).
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { isDesktopApp } from '../utils/apiMode';
import {
  getHardwareCapabilities,
  installComponent,
  listComponents,
  subscribeToInstallProgress,
  uninstallComponent,
} from '../services/componentService';
import type { ComponentInfo } from '../services/componentService';
import { handleError } from '../utils/errorHandler';

/** Coarse install stages the gates render, mapped from the backend's steps. */
export interface LocalModelInstallProgress {
  stage: 'preparing' | 'downloading' | 'extracting' | 'configuring' | 'complete' | 'error';
  percent: number;
  message: string;
}

/** What the backend really requires to run a local model. */
export interface LocalModelRequirements {
  vramGb: number;
  ramGb: number;
  diskGb: number;
  downloadMb: number;
  /** False when this machine's GPU/RAM/disk cannot meet the requirements above. */
  compatible: boolean;
}

export interface LocalModelInstallsState {
  /** Component ids reported INSTALLED by the backend. */
  installedModels: Set<string>;
  /**
   * Component ids this backend actually KNOWS about, i.e. that came back in
   * `/api/v1/components`. Not the same as "not installed": an id missing from
   * here has no manager to install it, so `POST .../install` can only fail.
   * Gates MUST require membership here before offering an install, or they
   * show a button that retries forever against a component that does not exist
   * on this build.
   */
  availableModels: Set<string>;
  /** Real requirements + hardware verdict per component id. */
  modelRequirements: Record<string, LocalModelRequirements>;
  installProgress: Record<string, LocalModelInstallProgress>;
  isLoading: boolean;
  error: string | null;
  /**
   * True only once the component API has actually answered. Until then
   * `installedModels` being empty means "we have not heard back", NOT
   * "nothing is installed" — see the header note.
   */
  stateKnown: boolean;
}

export interface LocalModelInstallsActions {
  installModel: (modelId: string) => Promise<void>;
  uninstallModel: (modelId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export type UseLocalModelInstallsReturn = LocalModelInstallsState & LocalModelInstallsActions;

/** Component status values that mean "the model is on disk and usable". */
const INSTALLED_STATUSES = new Set(['installed', 'running', 'stopped', 'updating']);

/**
 * Map the backend's install step names onto the UI's coarser stages.
 *
 * The managers all speak one vocabulary (utils/components/sfx/base_sfx_manager.py
 * and utils/components/f5_tts_manager.py):
 *   checking -> venv -> pytorch -> dependencies -> model -> complete | error
 *
 * Everything unrecognised used to fall through to 'downloading', so the card
 * said "Downloading..." while the backend was creating a virtualenv and
 * pip-installing PyTorch — most of the wall-clock time of an install, spent
 * lying about what was happening. Only the steps that really move bytes for
 * the model map to 'downloading' now.
 */
function toStage(step: string): LocalModelInstallProgress['stage'] {
  switch (step) {
    case 'complete':
      return 'complete';
    case 'error':
      return 'error';
    case 'extracting':
      return 'extracting';
    case 'venv':
    case 'pytorch':
    case 'dependencies':
    case 'installing':
    case 'configuring':
      return 'configuring';
    case 'model':
    case 'downloading':
      return 'downloading';
    default:
      // 'checking', 'starting', 'idle' and anything new: no bytes are moving
      // and nothing is being configured yet.
      return 'preparing';
  }
}

/**
 * Track real installation state for the given component ids.
 *
 * @param componentIds - Component ids from the backend registry
 *                       (utils/components/registry.py).
 */
export function useLocalModelInstalls(
  componentIds: readonly string[]
): UseLocalModelInstallsReturn {
  const [installedModels, setInstalledModels] = useState<Set<string>>(new Set());
  const [availableModels, setAvailableModels] = useState<Set<string>>(new Set());
  const [modelRequirements, setModelRequirements] = useState<
    Record<string, LocalModelRequirements>
  >({});
  const [installProgress, setInstallProgress] = useState<Record<string, LocalModelInstallProgress>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live SSE subscriptions, so an unmount never leaks an EventSource.
  const unsubscribersRef = useRef<Record<string, () => void>>({});

  // Only active in desktop mode
  const isActive = isDesktopApp();

  // Stable identity even when the caller passes a fresh array literal, so
  // checkModels (and the effect that runs it) doesn't re-fire every render.
  const idsKey = componentIds.join(',');
  const ids = useMemo(() => idsKey.split(',').filter(Boolean), [idsKey]);

  /**
   * Read real installation state + hardware compatibility from the backend.
   */
  const checkModels = useCallback(async () => {
    if (!isActive) {
      setAvailableModels(new Set());
      setInstalledModels(new Set());
      setModelRequirements({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [components, hardware] = await Promise.all([
        listComponents(),
        // Compatibility is advisory: if hardware detection fails we still show
        // the models (the backend re-checks for real before generating).
        getHardwareCapabilities().catch(() => null),
      ]);

      const available = new Set<string>();
      const installed = new Set<string>();
      const requirements: Record<string, LocalModelRequirements> = {};

      for (const componentId of ids) {
        const info: ComponentInfo | undefined = components[componentId];
        if (!info) continue;

        available.add(componentId);
        if (INSTALLED_STATUSES.has(info.status)) {
          installed.add(componentId);
        }
        requirements[componentId] = {
          vramGb: info.requirements.vram_gb ?? 0,
          ramGb: info.requirements.ram_gb ?? 0,
          diskGb: info.requirements.disk_gb ?? 0,
          downloadMb: info.sizeMb,
          compatible: hardware?.componentCompatibility?.[componentId] ?? true,
        };
      }

      setAvailableModels(available);
      setInstalledModels(installed);
      setModelRequirements(requirements);
      setHasAnswered(true);
    } catch (err) {
      console.error('Failed to check local models:', err);
      setError(handleError(err, 'LocalModelInstalls', 'Failed to check local models'));
    } finally {
      setIsLoading(false);
    }
  }, [isActive, ids]);

  /**
   * Install a model — starts the REAL download and streams its progress.
   */
  const installModel = useCallback(
    async (modelId: string) => {
      if (!isActive) {
        throw new Error('Local models only available in desktop mode');
      }
      if (!ids.includes(modelId)) {
        throw new Error(`Unknown model: ${modelId}`);
      }

      // Optimistic first frame, before the POST has even been answered — so it
      // must not claim a download is under way (the first real step is
      // 'checking', and an install can still fail before a byte is fetched).
      setInstallProgress((prev) => ({
        ...prev,
        [modelId]: { stage: 'preparing', percent: 0, message: 'Starting installation...' },
      }));

      try {
        await installComponent(modelId);
      } catch (err) {
        const message = handleError(err, 'LocalModelInstalls', 'Installation failed');
        setInstallProgress((prev) => ({
          ...prev,
          [modelId]: { stage: 'error', percent: 0, message },
        }));
        throw err;
      }

      unsubscribersRef.current[modelId]?.();
      unsubscribersRef.current[modelId] = subscribeToInstallProgress(
        modelId,
        (progress) => {
          setInstallProgress((prev) => ({
            ...prev,
            [modelId]: {
              stage: toStage(progress.step),
              percent: progress.progress,
              message: progress.message,
            },
          }));
        },
        () => {
          delete unsubscribersRef.current[modelId];
          // Re-read the authoritative status instead of assuming success.
          void checkModels();
        },
        (streamError) => {
          delete unsubscribersRef.current[modelId];
          setInstallProgress((prev) => ({
            ...prev,
            [modelId]: { stage: 'error', percent: 0, message: streamError.message },
          }));
        }
      );
    },
    [isActive, ids, checkModels]
  );

  /**
   * Uninstall a model.
   */
  const uninstallModel = useCallback(
    async (modelId: string) => {
      if (!isActive) {
        throw new Error('Local models only available in desktop mode');
      }

      await uninstallComponent(modelId);
      await checkModels();
    },
    [isActive, checkModels]
  );

  /**
   * Refresh model status
   */
  const refresh = useCallback(async () => {
    await checkModels();
  }, [checkModels]);

  // Check models on mount
  useEffect(() => {
    void checkModels();
  }, [checkModels]);

  // Close any open progress stream on unmount
  useEffect(() => {
    const unsubscribers = unsubscribersRef.current;
    return () => {
      Object.values(unsubscribers).forEach((unsubscribe) => unsubscribe());
    };
  }, []);

  return {
    installedModels,
    availableModels,
    modelRequirements,
    installProgress,
    isLoading,
    error,
    // Never "known" before the first answer: the very first render happens
    // before the mount effect runs, when isLoading is still false and every
    // set is empty. Gating on !isLoading alone would flash an install offer
    // for a model that is already on disk.
    stateKnown: isActive && hasAnswered && !isLoading && error === null,
    installModel,
    uninstallModel,
    refresh,
  };
}

export default useLocalModelInstalls;
